import './History.styles.scss';

import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Game from '../../components/Game/Game';
import GameLogs from '../../components/Game/GameLogs/GameLogs';
import TableHeader from '../../components/Game/TableHeader/TableHeader';
import SwitchBlindsChipsButton from '../../components/shared/Button/SwitchBlindsChipsButton/SwitchBlindsChipsButton';
import { ButtonType } from '../../components/shared/Button/TableButton.types';
import { useDisableWebsocketOnCurrentPage } from '../../hooks/useDisableWebsocketOnCurrentPage';
import useScreenDimensions from '../../hooks/useScreenDimensions';
import { TableStateChangeType } from '../../model/enumeration/TableStateChangeType.enum';
import botErrorStore from '../../store/BotErrorStore';
import chatStore from '../../store/ChatStore';
import commonStore from '../../store/CommonStore';
import eventLogsStore from '../../store/EventLogsStore';
import gameStore from '../../store/GameStore';
import playerActionStore from '../../store/PlayerActionStore';
import sessionStorageStore from '../../store/SessionStorageStore';
import { GameState } from '../../store/state/GameState';
import { GameTable } from '../../store/state/GameTable';
import stateHolder from '../../store/TableHistoryGameStateStore';
import tableStore from '../../store/TableStore';
import userSettingsStore from '../../store/UserSettingsStore';
import { dateFromString } from '../../utils/date.utils';
import { WEvent } from '../../websocket/events/WEvent';
import { WEventType } from '../../websocket/events/WEventType';
import { WTableSyncEvent } from '../../websocket/events/WTableSyncEvent';
import { WEventsDispatcher } from '../../websocket/handlers/WEventsDispatcher';

const History: FC = () => {
  const [searchParams] = useSearchParams();
  const [tableId] = useState(searchParams.get('tableId'));
  const [history, setHistory] = useState<WEvent[][]>([]);
  const [selectedGame, setSelectedGame] = useState(0);
  const [alreadyWaitedTimout, setAlreadyWaitedTimeout] = useState(0);

  const { screenWidth } = useScreenDimensions();

  const [nextEventIndex, setNextEventIndex] = useState(0);
  const [isDisabledRewind, setIsDisabledRewind] = useState(false);

  useDisableWebsocketOnCurrentPage();

  useEffect(() => {
    return () => {
      commonStore.clearAllTableData();
      sessionStorageStore.removeReadChatMessagesIds(tableId);
      sessionStorageStore.removeReadBotErrorIds(tableId);
    };
  }, []);

  const isGameFinished = (): boolean =>
    nextEventIndex === history[selectedGame]?.length;

  const mockGameAndTable = (tableSyncEvent: WTableSyncEvent): void => {
    tableSyncEvent.changeType = TableStateChangeType.UPDATE_PLAYERS;
    if (!tableStore.getActiveTableById(tableId)) {
      const table = new GameTable();
      table.initTable(tableSyncEvent.table as GameTable);
      tableStore.replaceTable(table);

      const game = new GameState();
      game.setTable(table);
      if (table.gameId) {
        game.setId(table.gameId);
      }
      gameStore.replaceGame(table.id, game);
    }
  };

  const updateEventTime = (event: WEvent): WEvent => {
    if (
      event.eventType === WEventType.CHAT_EVENT ||
      event.eventType === WEventType.BOT_ERROR_EVENT
    ) {
      return event;
    }
    const updatedTime = new Date().toISOString();
    return {
      ...event,
      eventTime: updatedTime,
    };
  };

  const processEvent = async (
    event: WEvent,
    eventIndex?: number,
  ): Promise<void> => {
    const updatedEvent = updateEventTime(event);
    if (updatedEvent.eventType === WEventType.TABLE_SYNC_EVENT) {
      mockGameAndTable(updatedEvent as WTableSyncEvent);
    }
    stateHolder.captureHolders();
    await WEventsDispatcher.handleEvent(updatedEvent);
    setNextEventIndex((eventIndex ?? nextEventIndex) + 1);
    setAlreadyWaitedTimeout(0);
  };

  const playFirstEvent = (history: WEvent[][], gameNumber: number): void => {
    const event = history[gameNumber][0];
    void processEvent(event, 0);
  };

  const initHistory = (value: WEvent[][]): void => {
    let filteredHistory = value;
    if (userSettingsStore.settings.chat.disabled) {
      filteredHistory = value.map(gameHistory =>
        gameHistory.filter(event => event.eventType !== WEventType.CHAT_EVENT),
      );
    }
    setHistory(filteredHistory);
  };

  const forceEvent = (eventIndex?: number): Promise<void> => {
    const event = history[selectedGame][eventIndex ?? nextEventIndex];
    commonStore.setPaused(true);
    return processEvent(event, eventIndex);
  };

  const playGame = async (): Promise<void> => {
    setIsDisabledRewind(true);
    for (
      let eventIndex = nextEventIndex;
      eventIndex < history[selectedGame].length;
      eventIndex++
    ) {
      await forceEvent(eventIndex);
    }
    setIsDisabledRewind(false);
  };

  const resetGameState = (page: number): void => {
    setNextEventIndex(0);
    commonStore.setPaused(true);
    setSelectedGame(page);
    playerActionStore.resetState();
    stateHolder.resetState();
    gameStore.clearAllGames();
    tableStore.resetState();
    chatStore.resetState();
    botErrorStore.resetState();
    eventLogsStore.emptyLogs(tableId);
    sessionStorageStore.removeReadChatMessagesIds(tableId);
    sessionStorageStore.removeReadBotErrorIds(tableId);
    playFirstEvent(history, page);
    gameStore.forceReloadGame();
  };

  useEffect(() => {
    commonStore.setPaused(true);
    tableStore.getTableHistory(tableId).then(history => {
      initHistory(history);
      sessionStorageStore.removeReadChatMessagesIds(tableId);
      sessionStorageStore.removeReadBotErrorIds(tableId);
      eventLogsStore.addEmptyLogs(tableId);
      playFirstEvent(history, 0);
    });
  }, []);

  useEffect(() => {
    if (!commonStore.paused && !isGameFinished()) {
      const nextEvent = history[selectedGame][nextEventIndex];
      const prevEvent = history[selectedGame][nextEventIndex - 1];

      // устанавливаем таймаут = разница между временем следующего события и текущего
      const nextEventTimeout =
        dateFromString(nextEvent.eventTime).valueOf() -
        dateFromString(prevEvent.eventTime).valueOf() -
        alreadyWaitedTimout;

      const startTime = Date.now();
      const timeout = setTimeout(
        () => processEvent(nextEvent),
        nextEventTimeout,
      );
      return () => {
        const waitedTime = Date.now() - startTime;
        if (waitedTime >= 0) {
          setAlreadyWaitedTimeout(waitedTime);
        }
        clearTimeout(timeout);
      };
    }
  }, [nextEventIndex, selectedGame, history, commonStore.paused]);

  const handleBackward = (): void => {
    commonStore.setPaused(true);
    setNextEventIndex(prevState => prevState - 1);
    setAlreadyWaitedTimeout(0);
    stateHolder.stepBackward();
  };

  const findViewingTable = useCallback(
    (tableId: string) => tableStore.getActiveTableById(tableId),
    [],
  );

  const handleChangePage = (event: PaginatorPageChangeEvent): void => {
    resetGameState(event.page);
  };

  const NavigationPanel: React.ReactNode = (
    <div className="nav-panel">
      <Button
        className="nav-button"
        rounded
        outlined
        raised
        icon="pi pi-fast-backward"
        onClick={() => resetGameState(selectedGame)}
        disabled={nextEventIndex === 1 || isDisabledRewind}
      />
      <Button
        className="nav-button"
        rounded
        outlined
        raised
        icon="pi pi-backward"
        onClick={handleBackward}
        disabled={nextEventIndex === 1 || isDisabledRewind}
      />
      <Button
        className="nav-button"
        outlined
        rounded
        raised
        icon={commonStore.paused ? 'pi pi-play' : 'pi pi-pause'}
        onClick={commonStore.togglePaused}
        disabled={isGameFinished() || isDisabledRewind}
      />
      <Button
        className="nav-button"
        outlined
        raised
        rounded
        icon="pi pi-forward"
        disabled={isGameFinished() || isDisabledRewind}
        onClick={() => forceEvent()}
      />
      <Button
        className="nav-button"
        outlined
        raised
        rounded
        icon="pi pi-fast-forward"
        disabled={isGameFinished() || isDisabledRewind}
        onClick={playGame}
      />
    </div>
  );

  return (
    <div className="History-Component">
      <SwitchBlindsChipsButton />
      {!!tableStore.activeTables.size && (
        <>
          <div className="content-container container-fluid">
            <div
              className={`row justify-content-center w-100 ${
                screenWidth <= 1399 ? 'table' : 'tables'
              }`}
            >
              <div className="col-xxl-5 col-10 position-relative">
                <TableHeader
                  table={findViewingTable(tableId)}
                  buttons={[
                    ButtonType.PROFITS,
                    ButtonType.BOT_ERRORS,
                    ButtonType.CHAT,
                    ButtonType.WAITING_HOST_BOTS,
                  ]}
                />
                <Game tableId={tableId} isolated />
                <div className="row justify-content-center">
                  {NavigationPanel}
                  <Paginator
                    alwaysShow
                    rows={1}
                    totalRecords={history.length}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={
                      history.length ? 'Total pages: {totalPages}' : ''
                    }
                    first={selectedGame}
                    onPageChange={handleChangePage}
                    pt={{
                      root: {
                        style: isDisabledRewind && {
                          opacity: 0.4,
                        },
                      },
                      firstPageButton: { disabled: isDisabledRewind },
                      prevPageButton: { disabled: isDisabledRewind },
                      nextPageButton: { disabled: isDisabledRewind },
                      lastPageButton: { disabled: isDisabledRewind },
                      pageButton: { disabled: isDisabledRewind },
                    }}
                  />
                </div>
              </div>
              <div className="col-xxl-5 col-11 mt-xxl-0 mt-5">
                <GameLogs isOpen tableId={tableId} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default observer(History);
